import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

export default function Exam() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<Record<number, any>>({});
  const [timeLeft, setTimeLeft] = useState(3600); // 60 minutes in seconds
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);

  const questions = [
    {
      id: 1,
      type: 'multiple-choice',
      question: 'ما هي قيمة المشتقة الأولى للدالة f(x) = x² + 3x + 2 عند x = 2؟',
      options: ['5', '7', '9', '11'],
      correctAnswer: 1
    },
    {
      id: 2,
      type: 'multiple-choice',
      question: 'أي من المعادلات التالية تمثل خط مستقيم؟',
      options: ['y = x²', 'y = 2x + 3', 'y = sin(x)', 'y = 1/x'],
      correctAnswer: 1
    },
    {
      id: 3,
      type: 'true-false',
      question: 'المصفوفة المربعة التي محددها يساوي صفر تكون قابلة للعكس',
      options: ['صح', 'خطأ'],
      correctAnswer: 1
    },
    {
      id: 4,
      type: 'multiple-choice',
      question: 'ما هو ناتج تكامل الدالة f(x) = 2x؟',
      options: ['x² + C', '2x² + C', 'x²/2 + C', '2'],
      correctAnswer: 0
    },
    {
      id: 5,
      type: 'multiple-choice',
      question: 'إذا كان A = {1, 2, 3} و B = {2, 3, 4}، فما هو A ∩ B؟',
      options: ['{1, 2, 3, 4}', '{2, 3}', '{1}', '{4}'],
      correctAnswer: 1
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          handleAutoSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleAnswer = (questionId: number, answer: number) => {
    setAnswers({ ...answers, [questionId]: answer });
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleAutoSubmit = () => {
    navigate(`/exam-result/${id}`);
  };

  const handleSubmit = () => {
    setShowSubmitConfirm(true);
  };

  const confirmSubmit = () => {
    navigate(`/exam-result/${id}`);
  };

  const progress = ((currentQuestion + 1) / questions.length) * 100;
  const answeredCount = Object.keys(answers).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-[#E8F5F3]">
      {/* Top Bar */}
      <div className="bg-white shadow-md sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 flex items-center justify-center bg-[#0F2A3A] rounded-xl">
                <i className="ri-file-list-3-line text-xl text-[#1ABC9C]"></i>
              </div>
              <div>
                <h1 className="text-lg font-bold text-[#0F2A3A]">الرياضيات المتقدمة</h1>
                <p className="text-sm text-slate-600">السؤال {currentQuestion + 1} من {questions.length}</p>
              </div>
            </div>

            <div className="flex items-center gap-6">
              <div className="flex items-center gap-3 px-4 py-2 bg-slate-100 rounded-xl">
                <i className="ri-time-line text-xl text-[#1ABC9C]"></i>
                <span className="text-lg font-bold text-[#0F2A3A]">{formatTime(timeLeft)}</span>
              </div>

              <div className="flex items-center gap-3 px-4 py-2 bg-slate-100 rounded-xl">
                <i className="ri-checkbox-circle-line text-xl text-[#1ABC9C]"></i>
                <span className="text-lg font-bold text-[#0F2A3A]">{answeredCount}/{questions.length}</span>
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mt-4">
            <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-[#1ABC9C] transition-all duration-300"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-8 mb-6">
            {/* Question */}
            <div className="mb-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 flex items-center justify-center bg-[#1ABC9C] rounded-xl flex-shrink-0">
                  <span className="text-xl font-bold text-white">{currentQuestion + 1}</span>
                </div>
                <div className="flex-1">
                  <span className="inline-block px-3 py-1 bg-blue-100 text-blue-700 text-sm font-semibold rounded-full mb-3">
                    {questions[currentQuestion].type === 'multiple-choice' ? 'اختيار من متعدد' : 'صح أو خطأ'}
                  </span>
                  <h2 className="text-2xl font-bold text-[#0F2A3A] leading-relaxed">
                    {questions[currentQuestion].question}
                  </h2>
                </div>
              </div>

              {/* Options */}
              <div className="space-y-3">
                {questions[currentQuestion].options.map((option, index) => (
                  <button
                    key={index}
                    onClick={() => handleAnswer(questions[currentQuestion].id, index)}
                    className={`w-full p-5 text-right rounded-xl border-2 transition-all duration-300 cursor-pointer ${
                      answers[questions[currentQuestion].id] === index
                        ? 'border-[#1ABC9C] bg-[#1ABC9C]/10'
                        : 'border-slate-200 hover:border-[#1ABC9C]/50 hover:bg-slate-50'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                        answers[questions[currentQuestion].id] === index
                          ? 'border-[#1ABC9C] bg-[#1ABC9C]'
                          : 'border-slate-300'
                      }`}>
                        {answers[questions[currentQuestion].id] === index && (
                          <i className="ri-check-line text-white text-sm"></i>
                        )}
                      </div>
                      <span className={`text-lg ${
                        answers[questions[currentQuestion].id] === index
                          ? 'text-[#0F2A3A] font-semibold'
                          : 'text-slate-700'
                      }`}>
                        {option}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Navigation Buttons */}
            <div className="flex items-center justify-between pt-6 border-t border-slate-200">
              <button
                onClick={handlePrevious}
                disabled={currentQuestion === 0}
                className={`px-6 py-3 rounded-xl font-semibold transition-all whitespace-nowrap ${
                  currentQuestion === 0
                    ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                    : 'bg-slate-200 text-slate-700 hover:bg-slate-300 cursor-pointer'
                }`}
              >
                <i className="ri-arrow-right-line ml-2"></i>
                السابق
              </button>

              <div className="flex items-center gap-3">
                {currentQuestion === questions.length - 1 ? (
                  <button
                    onClick={handleSubmit}
                    className="px-8 py-3 bg-green-600 text-white rounded-xl font-semibold hover:bg-green-700 transition-all whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-send-plane-line ml-2"></i>
                    إرسال الإجابات
                  </button>
                ) : (
                  <button
                    onClick={handleNext}
                    className="px-6 py-3 bg-[#1ABC9C] text-white rounded-xl font-semibold hover:bg-[#16a085] transition-all whitespace-nowrap cursor-pointer"
                  >
                    التالي
                    <i className="ri-arrow-left-line mr-2"></i>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Question Navigator */}
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <h3 className="text-lg font-bold text-[#0F2A3A] mb-4">التنقل بين الأسئلة</h3>
            <div className="grid grid-cols-10 gap-2">
              {questions.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentQuestion(index)}
                  className={`w-10 h-10 rounded-lg font-semibold transition-all cursor-pointer ${
                    currentQuestion === index
                      ? 'bg-[#1ABC9C] text-white'
                      : answers[questions[index].id] !== undefined
                      ? 'bg-green-100 text-green-700'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {index + 1}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Submit Confirmation Modal */}
      {showSubmitConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full">
            <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center bg-yellow-100 rounded-full">
              <i className="ri-alert-line text-4xl text-yellow-600"></i>
            </div>
            <h2 className="text-2xl font-bold text-[#0F2A3A] text-center mb-4">
              تأكيد إرسال الإجابات
            </h2>
            <p className="text-slate-600 text-center mb-6 leading-relaxed">
              هل أنت متأكد من إرسال إجاباتك؟ لن تتمكن من تعديلها بعد الإرسال.
            </p>
            <div className="space-y-3">
              <p className="text-center text-sm text-slate-600">
                عدد الأسئلة المجابة: <span className="font-bold text-[#1ABC9C]">{answeredCount}</span> من {questions.length}
              </p>
              {answeredCount < questions.length && (
                <p className="text-center text-sm text-red-600">
                  لديك {questions.length - answeredCount} أسئلة لم تجب عليها
                </p>
              )}
            </div>
            <div className="flex gap-3 mt-8">
              <button
                onClick={() => setShowSubmitConfirm(false)}
                className="flex-1 py-3 bg-slate-200 text-slate-700 rounded-xl font-semibold hover:bg-slate-300 transition-all whitespace-nowrap cursor-pointer"
              >
                إلغاء
              </button>
              <button
                onClick={confirmSubmit}
                className="flex-1 py-3 bg-[#1ABC9C] text-white rounded-xl font-semibold hover:bg-[#16a085] transition-all whitespace-nowrap cursor-pointer"
              >
                تأكيد الإرسال
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
